---
id: runbook-generator
title: Runbook Generator
category: incident
difficulty: beginner
tags:
  - runbook
  - documentation
  - operations
  - on-call
  - procedures
description: >
  Generates a structured operational runbook for a service or alert,
  with diagnostic steps and remediation procedures.
whenToUse: >
  When setting up new services, onboarding on-call engineers, or when
  an incident reveals a missing runbook.
variables:
  - name: alert_name
    desc: Name of the alert or issue type
  - name: service_name
    desc: Service this runbook covers
  - name: trigger_description
    desc: What typically causes this alert
  - name: severity
    desc: Typical severity level
  - name: architecture
    desc: Brief architecture description (what talks to what)
  - name: tools
    desc: Tools available for diagnosis and remediation
tips:
  - Write for the worst case — assume the reader is tired, unfamiliar with the service, and stressed.
  - Include exact commands, not just descriptions. "Run kubectl get pods" is better than "check pod status".
  - Add expected output examples so the reader knows what normal vs abnormal looks like.
---

You are an SRE writing an operational runbook. Create a runbook for the following scenario.

## Scenario
- Alert/Issue: {{alert_name}}
- Service: {{service_name}}
- Typical trigger: {{trigger_description}}
- Severity when triggered: {{severity}}

## Service Architecture
{{architecture}}

## Available Tools
{{tools}} (kubectl, AWS CLI, Terraform, Grafana, PagerDuty, etc.)

## Task
Create a runbook that an on-call engineer can follow at 3 AM with:

1. **Overview** — What this alert means, why it matters
2. **Quick Assessment** (< 2 min) — First commands to run to understand scope
3. **Diagnostic Steps** — Systematic investigation checklist
4. **Remediation** — Step-by-step fix for common causes
5. **Escalation** — When and who to escalate to
6. **Verification** — How to confirm the issue is resolved
7. **Prevention** — Long-term fixes to prevent recurrence

Each step should include the exact commands to run.

<!-- EXAMPLE_OUTPUT -->

# Runbook: HighMemoryUsage — payment-service

## Overview
This alert fires when payment-service pod memory exceeds 85% of its limit (2Gi).
Left unaddressed, the pod will be OOMKilled, causing request failures.

## Quick Assessment (< 2 min)
```bash
kubectl top pods -l app=payment-service -n payments
kubectl get events -n payments --sort-by=.lastTimestamp | head -20
```

## Diagnostic Steps
1. Check current memory usage trend:
   ```bash
   kubectl exec -n payments deploy/payment-service -- cat /sys/fs/cgroup/memory/memory.usage_in_bytes
   ```
...
